import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { searchManyInputStyles } from '@core/components/fc-input/base/fc-search-many-input/fc-search-many-input.component';
import { normalizeAddress } from '@core/utils/normalize-Address';
import { PostalCodeUtilsService } from '@core/utils/postal-code';
import { QueryAddressCoordinatesGQL, QueryAddressPostalCodeGQL, QueryCoordinatesGeocodeGQL } from 'app/main/locations/services/location.service';
import brAddressFormatter from 'br-address-formatter';
import { emit } from 'process';
import { merge } from 'rxjs';
import { debounceTime, filter, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'tab-general',
  templateUrl: './tab-general.component.html',
  styleUrls: ['./tab-general.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TabGeneralComponent implements OnInit {
  @Input() locationForm: FormGroup;
  @Input() isViewMode: boolean;

  public postalCodeIsLoading = false;
  public desiredStyle: searchManyInputStyles = searchManyInputStyles.Underline;

  constructor(
    private postalCodeUtilsService: PostalCodeUtilsService,
    private queryAddressPostalCodeGQL: QueryAddressPostalCodeGQL,
    private queryAddressCoordinatesGQL: QueryAddressCoordinatesGQL
  ) { }

  ngOnInit(): void {
    this.locationForm.get('address').get('postalCode').valueChanges.pipe(
      debounceTime(1000),
      filter((postalCode) => this.postalCodeUtilsService.isValidFormat(postalCode) && postalCode.length === 8),
    ).subscribe(async (postalCode) => {
      if (this.locationForm.value.searchingWithGoogle) { return; }
      this.postalCodeIsLoading = true;

      this.locationForm.get('address').get('city').setValue(null);
      this.locationForm.get('address').get('streetName').setValue(null);
      this.locationForm.get('address').get('neighborhood').setValue(null);
      this.locationForm.get('address').get('state').setValue(null);

      const address = await this.queryAddressPostalCodeGQL.fetch({ postalCode })
        .toPromise()
        .then((response) => response.data && response.data.addressPostalCode)
        .catch(() => null);

      if (address) {
        this.locationForm.get('address').get('city').setValue(address.city);
        this.locationForm.get('address').get('streetName').setValue(address.streetName);
        this.locationForm.get('address').get('neighborhood').setValue(address.neighborhood);
        this.locationForm.get('address').get('state').setValue(address.state);
      }

      this.postalCodeIsLoading = false;
    });

    merge(
      this.locationForm.get('address').get('city').valueChanges,
      this.locationForm.get('address').get('streetName').valueChanges,
      this.locationForm.get('address').get('neighborhood').valueChanges,
      this.locationForm.get('address').get('state').valueChanges,
      this.locationForm.get('address').get('number').valueChanges,
    ).pipe(
      debounceTime(1000),
      map(() => {
        return brAddressFormatter({
          ...this.locationForm.get('address').value,
          street: this.locationForm.get('address').value.streetName
        });
      }),
      filter((formattedAddress) => !!formattedAddress),
      switchMap((formattedAddress) => {
        return this.queryAddressCoordinatesGQL.fetch({ address: formattedAddress })
          .toPromise()
          .catch(() => null)
          .then((response) => {
            return response && response.data && response.data.addressCoordinates;
          });
      })
    ).subscribe((coordinates) => {
      const latitude = (coordinates && coordinates.latitude) || null;
      const longitude = (coordinates && coordinates.longitude) || null;

      this.locationForm.get('address').get('coords').get('latitude').setValue(latitude);
      this.locationForm.get('address').get('coords').get('longitude').setValue(longitude);
      this.locationForm.get('address').get('formattedAddress').setValue(brAddressFormatter({
        ...this.locationForm.get('address').value,
        street: this.locationForm.get('address').value.streetName
      }));
    });
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^a-zA-Z0-9]/g, '');
  }
}
